/**
 * How long a gap ends a run.
 *
 * Five minutes: two lines typed in the same breath are one utterance and should not repeat the
 * name and the time, whereas a reply half an hour later is a new thing to say and reads wrongly
 * when it is glued to the message above it.
 */
export const MESSAGE_GROUPING_GAP_MINUTES = 5;
/**
 * A thread split into days, and each day into runs.
 *
 * The input is expected oldest-first, which is the order both apps assemble their pages into. A
 * caller that hands it the other order gets sections in that order back rather than a silent
 * re-sort: reordering somebody's conversation is not this function's business.
 */
export function groupMessagesByDay(messages, gapMinutes = MESSAGE_GROUPING_GAP_MINUTES) {
    const gapMs = gapMinutes * 60_000;
    const sections = [];
    for (const message of messages) {
        const day = localDay(message.createdAt);
        let section = sections.at(-1);
        if (!section || section.day !== day) {
            section = { day, startedAt: message.createdAt, runs: [] };
            sections.push(section);
        }
        const run = section.runs.at(-1);
        if (run && continuesRun(run, message, gapMs)) {
            run.messages.push(message);
            continue;
        }
        section.runs.push({
            key: message.id,
            senderId: message.sender?.id ?? null,
            isSystem: message.systemKind !== null,
            messages: [message],
        });
    }
    return sections;
}
/**
 * Whether this message belongs to the run above it.
 *
 * A system note never joins one and never takes one: "a call was started" between two of Priya's
 * lines is a change of speaker in both directions, and hiding her name on the line after it would
 * make the note look like hers.
 */
function continuesRun(run, message, gapMs) {
    if (run.isSystem || message.systemKind !== null) {
        return false;
    }
    const senderId = message.sender?.id ?? null;
    if (senderId === null || run.senderId !== senderId) {
        return false;
    }
    const previous = run.messages.at(-1);
    if (!previous) {
        return false;
    }
    const gap = Date.parse(message.createdAt) - Date.parse(previous.createdAt);
    return Number.isFinite(gap) && gap >= 0 && gap <= gapMs;
}
/**
 * The calendar day an instant falls on **where the reader is**, not in UTC.
 *
 * A message sent at 23:30 in Delhi is an 18:00 UTC timestamp, and slicing the ISO string would
 * file it under the previous day for the person who wrote it. Built from the local parts rather
 * than from `toISOString`, which converts back to UTC and reintroduces the bug.
 */
function localDay(iso) {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) {
        // An unparseable timestamp gets its own section rather than joining somebody else's day.
        return iso;
    }
    const month = `${at.getMonth() + 1}`.padStart(2, '0');
    const date = `${at.getDate()}`.padStart(2, '0');
    return `${at.getFullYear()}-${month}-${date}`;
}
/**
 * Today, yesterday, or the date — the heading a day section carries.
 *
 * `now` is a parameter rather than a `new Date()` inside, because a function that reads the clock
 * cannot be tested for the one case that matters: the boundary.
 */
export function dayHeading(day, now = new Date()) {
    const today = localDay(now.toISOString());
    if (day === today) {
        return 'Today';
    }
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return day === localDay(yesterday.toISOString()) ? 'Yesterday' : null;
}
//# sourceMappingURL=message-grouping.js.map